import type {
  Direct4WDeliveryType,
  Direct4WStopIntent,
  Direct4WStopType,
  ShipmentExim,
} from '@/services/api/shipments';
import type {
  WorkflowStopIntent,
  WorkflowSummary,
} from '@/services/api/stopWorkflows';
import type { RoutePlanStop } from '@/services/api/routePlans';
import { kindOf } from '@/services/api/routeStopKind';

/**
 * The Direct-4W wizard's local stop model. Lives outside the modal so the route
 * shapes and the origin-mirroring rule can be exercised without driving five
 * required Step-1 widgets first.
 */

export interface StopDraft {
  uid: string;
  type: Direct4WStopType;
  /**
   * Only meaningful for DROP_OFF; kept for both so a segment toggle is
   * lossless. The workflow vocabulary (CR-6): UNLOAD (bongkar) /
   * RETURN_CONTAINER / GENERIC (a plain drop, e.g. a route plan's pool leg for a
   * charged client). The wire only knows UNLOAD | RETURN_CONTAINER, so GENERIC
   * is sent as "no intent" — see `shipmentStopIntent`.
   */
  intent: WorkflowStopIntent;
  name: string;
  address: string;
  latitude?: number;
  longitude?: number;
  /** '' = none picked. */
  workflowId: string;
  /**
   * Whether `workflowId` is an ops MANUAL pick (true) or an auto-resolved
   * default (false). Auto picks are re-resolved when the shipment's exim
   * direction — or the loaded workflow set — changes; a manual pick is never
   * overwritten. Before exim, "non-empty" alone meant "leave it", which would
   * freeze a wrong-direction default onto the stop the moment ops flipped
   * Export↔Import in Step 1.
   */
  workflowManual: boolean;
  /**
   * CR5-D3: `workflowId` is a route-plan PIN brought in by "Isi dari rute". Sent
   * as `allowWorkflowFallback: true` so a pin that went stale falls back to the
   * slot default (flagged) instead of failing the create. Treated like a manual
   * pick for the re-resolve rule (a plan pin is deliberate), and cleared the
   * moment ops picks a workflow by hand — a hand pick keeps the hard-fail.
   */
  workflowFromPlan: boolean;
  /**
   * The container goes back where it came from, so a RETURN_CONTAINER stop
   * mirrors the origin instead of being typed twice. Stays true until ops edits
   * this stop's address by hand, after which the stop is theirs.
   */
  linkedToOrigin: boolean;
}

let uidCounter = 0;
const nextUid = () => `stop-${++uidCounter}`;

export const makeStop = (
  type: Direct4WStopType,
  intent: WorkflowStopIntent = 'UNLOAD',
  linkedToOrigin = false
): StopDraft => ({
  uid: nextUid(),
  type,
  intent,
  name: '',
  address: '',
  workflowId: '',
  workflowManual: false,
  workflowFromPlan: false,
  linkedToOrigin,
});

// Single Drop = 1 PICKUP + 1 DROP_OFF · Multi Drop = 1 PICKUP + 2 DROP_OFF ·
// Multi Pickup = 2 PICKUP + 1 DROP_OFF.
//
// Multi Drop is the Import loop (port CY → customer → back to CY), so its last
// leg defaults to RETURN_CONTAINER linked to the origin. Multi Pickup is Export
// — its single drop is the port itself, not a container return, so it stays a
// plain UNLOAD that ops fills in.
export const makeStops = (deliveryType: Direct4WDeliveryType): StopDraft[] => {
  switch (deliveryType) {
    case 'MULTI_DROP':
      return [
        makeStop('PICKUP'),
        makeStop('DROP_OFF', 'UNLOAD'),
        makeStop('DROP_OFF', 'RETURN_CONTAINER', true),
      ];
    case 'MULTI_PICKUP':
      return [makeStop('PICKUP'), makeStop('PICKUP'), makeStop('DROP_OFF')];
    case 'SINGLE_DROP':
    default:
      return [makeStop('PICKUP'), makeStop('DROP_OFF')];
  }
};

/** Smallest legal route: an origin and a destination. */
export const MIN_STOPS = 2;

/** R3: append a stop (default a plain DROP_OFF) for a manually-built route. */
export const addStop = (
  stops: StopDraft[],
  type: Direct4WStopType = 'DROP_OFF'
): StopDraft[] =>
  syncLinkedStops([...stops, makeStop(type, type === 'PICKUP' ? 'UNLOAD' : 'GENERIC')]);

/** R3: remove a stop by uid, never dropping below `MIN_STOPS`. */
export const removeStop = (stops: StopDraft[], uid: string): StopDraft[] => {
  if (stops.length <= MIN_STOPS) return stops;
  return syncLinkedStops(stops.filter((s) => s.uid !== uid));
};

/** The four location fields a linked return stop copies from the origin. */
export const originMirror = (origin: StopDraft): Partial<StopDraft> => ({
  name: origin.name,
  address: origin.address,
  latitude: origin.latitude,
  longitude: origin.longitude,
});

/**
 * Copy the first PICKUP into every stop still linked to it. Runs on each stop
 * edit rather than once at intent-selection time: ops fill the cards in either
 * order, and a one-shot copy would leave the return stop empty whenever the
 * origin is picked afterwards — which then fails Step-2 validation for missing
 * coordinates with no visible cause.
 */
export const syncLinkedStops = (stops: StopDraft[]): StopDraft[] => {
  const origin = stops.find((s) => s.type === 'PICKUP');
  if (!origin) return stops;
  return stops.map((s) =>
    s.linkedToOrigin && s.uid !== origin.uid
      ? { ...s, ...originMirror(origin) }
      : s
  );
};

// Driver-facing label for the chip chain / review (a GENERIC drop reads like a
// plain delivery to the driver).
export const driverLabel = (stop: StopDraft): string => {
  if (stop.type === 'PICKUP') return 'Pickup';
  return stop.intent === 'RETURN_CONTAINER' ? 'Drop' : 'Delivery';
};

/**
 * The `intent` the create payload carries for a stop. The BE
 * `Direct4WStopIntent` is UNLOAD | RETURN_CONTAINER only, so a PICKUP and a
 * GENERIC drop are sent WITHOUT `intent` (the server resolves the GENERIC slot
 * default) — never invent a wire value the DTO rejects.
 */
export const shipmentStopIntent = (
  stop: Pick<StopDraft, 'type' | 'intent'>
): Direct4WStopIntent | undefined =>
  stop.type === 'DROP_OFF' && stop.intent !== 'GENERIC' ? stop.intent : undefined;

/**
 * The workflow a stop would resolve to on the server if ops picked none, for
 * the shipment's `exim` direction.
 *
 * Mirrors `resolveWorkflow()` in create-direct4w.usecase.ts EXACTLY. A client
 * can now hold a direction-specific default per (stopType, intent) — a Patimban
 * Export UNLOAD and an Import UNLOAD side by side — so the resolver walks the
 * same four slots, most specific first, and takes the first that has a default:
 *
 *   1. (intent, exim)      2. (intent, GENERAL)
 *   3. (GENERIC, exim)     4. (GENERIC, GENERAL)
 *
 * PICKUP is always GENERIC; DROP_OFF uses its own intent. The GENERAL rungs are
 * what keep a pre-exim client (only GENERAL rows) resolving exactly as before.
 * Keep the two in step — if they drift, the wizard shows one workflow and the
 * driver gets another.
 */
export const defaultWorkflowFor = (
  stop: StopDraft,
  workflows: Record<Direct4WStopType, WorkflowSummary[]>,
  exim: ShipmentExim = 'GENERAL'
): WorkflowSummary | undefined => {
  const defaults = (workflows[stop.type] ?? []).filter((w) => w.isDefault);
  const intent: WorkflowStopIntent =
    stop.type === 'DROP_OFF' ? stop.intent : 'GENERIC';

  const slots: Array<[WorkflowStopIntent, ShipmentExim]> = [];
  const pushSlot = (i: WorkflowStopIntent, e: ShipmentExim) => {
    if (!slots.some(([si, se]) => si === i && se === e)) slots.push([i, e]);
  };
  pushSlot(intent, exim);
  pushSlot(intent, 'GENERAL');
  pushSlot('GENERIC', exim);
  pushSlot('GENERIC', 'GENERAL');

  for (const [i, e] of slots) {
    const hit = defaults.find((w) => w.intent === i && w.exim === e);
    if (hit) return hit;
  }
  return undefined;
};

/**
 * Fill in each stop's default workflow for the given `exim`, leaving MANUAL
 * picks alone (see `StopDraft.workflowManual`). Ops asked to review a filled
 * form rather than an empty one, and preselecting also means the ID they SAW is
 * the ID submitted — a blank submit would re-resolve on the server and could
 * apply a different workflow if the default moved meanwhile.
 *
 * Auto picks are deliberately re-resolvable: flipping Export↔Import in Step 1
 * must swap each stop's default to the matching-direction workflow, not keep the
 * one resolved for the old direction.
 */
export const applyDefaultWorkflows = (
  stops: StopDraft[],
  workflows: Record<Direct4WStopType, WorkflowSummary[]>,
  exim: ShipmentExim = 'GENERAL'
): StopDraft[] =>
  stops.map((s) =>
    s.workflowManual
      ? s
      : { ...s, workflowId: defaultWorkflowFor(s, workflows, exim)?.id ?? '' }
  );

/**
 * Fill a stop's location from an address-master lane endpoint. Coordinates are
 * the point of this: a hand-typed address has none, and Step-2 validation
 * rejects a stop without them, so a text-only prefill still leaves ops
 * pin-dropping every stop by hand.
 */
export const applyLaneEndpoint = (
  stop: StopDraft,
  endpoint: {
    name: string;
    address: string;
    latitude: number;
    longitude: number;
  }
): StopDraft => ({
  ...stop,
  name: endpoint.name,
  address: endpoint.address,
  latitude: endpoint.latitude,
  longitude: endpoint.longitude,
});

/**
 * Prefill the origin (first stop) and the customer destination with the NAMES a
 * UJP import carries — text only; coordinates are then upgraded from the address
 * master by `applyLaneFromUjp`.
 *
 * The destination lands on the last stop that is NOT mirroring the origin: on
 * Multi Drop that is the middle UNLOAD leg, since the container-return leg is
 * filled from the origin instead. Shared by the import handler AND the
 * delivery-type switch — switching type rebuilds the stop set from scratch, so
 * it must re-apply this exact prefill or the imported addresses vanish. Keeping
 * one implementation stops the two paths drifting.
 */
export const prefillEndpoints = (
  stops: StopDraft[],
  originName: string,
  destinationName: string
): StopDraft[] => {
  if (!stops.length) return stops;
  const next = [...stops];
  next[0] = { ...next[0], name: originName, address: originName };
  const dest = next.reduce(
    (found, s, i) => (i > 0 && !s.linkedToOrigin ? i : found),
    -1
  );
  if (dest > 0) {
    next[dest] = {
      ...next[dest],
      name: destinationName,
      address: destinationName,
    };
  }
  return next;
};

/**
 * The address master's lane key: origin and destination names, each trimmed,
 * lowercased and inner-whitespace-collapsed, joined with `|`.
 *
 * Must stay identical to `StringHelper.buildNameKey` /
 * `StringHelper.normalizeKeyText` in nest-logistic-service — that pair is what
 * the server dedups lanes by, so a divergence here silently stops matching
 * lanes that plainly exist.
 */
export const laneNameKey = (originName: string, destinationName: string) =>
  [originName, destinationName].map(normalizePlaceName).join('|');

/** The shared normalization both keys use: trim, lowercase, collapse spaces. */
const normalizePlaceName = (p: string) =>
  (p ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Labels that name the SAME physical place across sources, `alias → canonical`,
 * both already normalized. The address master was seeded with one name per
 * place, but the real UJP export can label the same place differently — its
 * `origin` column says "AGL Patimban" where the master lane says "Patimban
 * Port". Under exact name-key matching that misses a lane that plainly exists,
 * and autofill silently falls back to a coordinate-less text prefill.
 *
 * Applied symmetrically to both sides of the lane comparison (see
 * `laneMatchKey`), so it only ever WIDENS a match — it never rewrites what is
 * stored, submitted, or shown. Add a row here per confirmed synonym; keep the
 * canonical side equal to the master's own name.
 */
const PLACE_ALIASES: Record<string, string> = {
  'agl patimban': 'patimban port',
};

/** Normalize a place name, then fold it onto its canonical alias if any. */
export const canonicalPlaceName = (name: string): string => {
  const n = normalizePlaceName(name);
  return PLACE_ALIASES[n] ?? n;
};

/**
 * Lane key for UJP autofill matching: like `laneNameKey`, but each place name is
 * canonicalized through `PLACE_ALIASES` first, so a UJP label and the master's
 * name for the same place collapse to one key. Use this — never `laneNameKey` —
 * when matching an imported name pair against master lanes. `laneNameKey` stays
 * a pure mirror of the BE dedup key; aliasing lives only in this lookup layer.
 */
export const laneMatchKey = (originName: string, destinationName: string) =>
  [originName, destinationName].map(canonicalPlaceName).join('|');

/**
 * Map a route plan to the 4W wizard's stop set (CR2-D7, "Isi dari rute"). The
 * client rule decides which stops become shipment stops: ALL of them when the
 * client is charged for pool positioning, otherwise the `pool` stops are
 * dropped — mirroring the server rule applied at UJP approve so the two never
 * drift (CR6-D3).
 *
 * The plan stop's `type` and `intent` go straight through (`kindOf` derives
 * them from a legacy `role`-only snapshot): a charged client's pool DROP_OFF
 * arrives as a plain GENERIC drop — no more forced RETURN_CONTAINER — and a
 * container return is only what the plan explicitly marked so. Every stop
 * carries its own coordinates, so none mirrors the origin and Step-2
 * validation passes without re-pinning.
 *
 * CR-5: a stop whose plan carries a `workflowId` brings the pin along, marked
 * `workflowFromPlan` (+ `workflowManual` so `applyDefaultWorkflows` leaves it
 * alone); an unpinned stop stays auto-resolvable to the client default.
 */
export const shipmentStopsFromPlan = (
  plan: { stops: RoutePlanStop[] },
  // CR-8: kept for call-site compatibility, no longer consulted. Billing
  // (`kmCharged`) and the stop list are separate questions: every stop is
  // driven, so every stop ships and records its arrived_at / resolved_at.
  _chargedPositioning?: boolean
): StopDraft[] => {
  const kept = [...plan.stops].sort((a, b) => a.seq - b.seq);
  return kept.map((s) => {
    const kind = kindOf(s);
    const pinned = s.workflowId ? s.workflowId : '';
    return {
      ...makeStop(kind.type, kind.intent, false),
      name: s.name,
      address: s.address,
      latitude: s.latitude,
      longitude: s.longitude,
      ...(pinned
        ? { workflowId: pinned, workflowManual: true, workflowFromPlan: true }
        : {}),
    };
  });
};

/**
 * The plan stops "Isi dari rute" leaves out. Since CR-8 that is none: the pool
 * legs ride along whether or not the client is billed for them. Kept so a
 * caller can still render the (now always empty) "tidak dibawa" note.
 */
export const droppedPlanStops = (
  _plan: { stops: RoutePlanStop[] },
  _chargedPositioning?: boolean
): RoutePlanStop[] => [];

/**
 * The distinct workflow ids currently attached to stops, in stop order.
 *
 * The milestone preview is loaded from this rather than from the picker's
 * onChange. Fetching on the click only covered workflows ops clicked, so a
 * default that preselected itself — or one that arrived with a UJP import —
 * left its card stuck on "Memuat preview…" indefinitely. Deriving the list
 * from stop STATE means every path that can select a workflow is covered,
 * including ones added later.
 */
export const selectedWorkflowIds = (stops: StopDraft[]): string[] =>
  Array.from(new Set(stops.map((s) => s.workflowId).filter(Boolean)));
